import type { ReactNode } from "react";
import { IntlShape } from "react-intl";
import { msgs } from "../../msgs";
import {
  ChainUpdateEntry,
  CreatePromotionInput,
  InventoryScopeDescriptor,
  PromotionChanges,
  WindowRange,
} from "../../types";

/**
 * Best-effort Spanish pluralizer for arbitrary property-defined nouns/adjectives (accommodation/
 * condition/configuration names come straight from THIS property's own inventory data, so there is
 * no fixed vocabulary to special-case). Matches the common cases well ("Doble" -> "Dobles",
 * "Triple" -> "Triples", "No reembolsable" -> "No reembolsables"). Deliberately conservative: only
 * touches values that LOOK like a simple word/short adjective (letters/accents/spaces/apostrophes
 * only) — a longer descriptive phrase with parentheses, digits or other punctuation (e.g. a meal
 * plan's "Con desayuno (no se incluyen bebidas)") is returned UNCHANGED rather than risk mangling
 * it by blindly appending a suffix to the end of the whole string (a real, reported bug: that
 * exact meal-plan name coming out as "...bebidas)es"). Already-plural values (ending in "s") are
 * also left untouched.
 */
function pluralizeEs(word: string): string {
  if (!/^[a-zA-ZÀ-ÿ' ]+$/.test(word)) return word;
  if (/s$/i.test(word)) return word;
  if (/[aeiouáéíóú]$/i.test(word)) return `${word}s`;
  return `${word}es`;
}

/** Joins several DISTINCT canonical names (each best-effort pluralized) with "o" — e.g. "Dobles o Suites". */
function joinOr(names: string[]): string {
  return names.map(pluralizeEs).join(" o ");
}

/**
 * Joins meal-plan names RAW (never pluralized) — a meal plan is a service description ("Con
 * desayuno", "Media pensión"), not a room-type adjective, so it doesn't need to grammatically
 * agree in number with "Habitaciones" the way accommodation/condition do, and is far more likely
 * to be a longer descriptive phrase (see `pluralizeEs`'s own doc comment) that must never be
 * mangled.
 */
function joinMealPlanOr(names: string[]): string {
  return names.join(" o ");
}

/** Builds "2 adultos y 1 niño" from a fixed occupancy combination, omitting zero categories. */
function formatOccupancyPhrase(
  occupancy: NonNullable<InventoryScopeDescriptor["occupancy"]>,
  intl: IntlShape
): string {
  const parts = [
    occupancy.adults > 0 &&
      intl.formatMessage(msgs.approvalOccupancyAdults, { count: occupancy.adults }),
    occupancy.children > 0 &&
      intl.formatMessage(msgs.approvalOccupancyChildren, { count: occupancy.children }),
    occupancy.juniors > 0 &&
      intl.formatMessage(msgs.approvalOccupancyJuniors, { count: occupancy.juniors }),
    occupancy.seniors > 0 &&
      intl.formatMessage(msgs.approvalOccupancySeniors, { count: occupancy.seniors }),
    occupancy.babies > 0 &&
      intl.formatMessage(msgs.approvalOccupancyBabies, { count: occupancy.babies }),
  ].filter((p): p is string => Boolean(p));
  if (parts.length === 0) return "";
  if (parts.length === 1) return parts[0];
  return `${parts.slice(0, -1).join(", ")} y ${parts[parts.length - 1]}`;
}

/**
 * Builds the specific inventory-scope legend ("Habitaciones Dobles", "Habitaciones no
 * reembolsables", "Habitaciones de 2 adultos y 1 niño", or a combination like "Habitaciones Dobles
 * no reembolsables") from a server-computed `InventoryScopeDescriptor` (see its own doc comment in
 * types.ts — every value in it is copied verbatim from a REAL matched inventory line, never worded
 * by the model). The FIRST present attribute, in this fixed priority order, anchors the phrase
 * ("Habitaciones <anchor>"); every OTHER present attribute is appended as a trailing modifier in
 * the SAME order. Returns null when the descriptor is absent/empty, so callers fall back to the
 * generic `approvalSelectedRates` text.
 */
export function describeInventoryScope(
  descriptor: InventoryScopeDescriptor | null | undefined,
  intl: IntlShape
): string | null {
  if (!descriptor) return null;
  const occupancyPhrase = descriptor.occupancy
    ? formatOccupancyPhrase(descriptor.occupancy, intl)
    : "";

  const anchors: { present: boolean; base: () => string; append: (base: string) => string }[] = [
    {
      present: Boolean(descriptor.accommodation?.length),
      base: () =>
        intl.formatMessage(msgs.approvalScopeAccommodation, {
          names: joinOr(descriptor.accommodation!),
        }),
      append: base =>
        intl.formatMessage(msgs.approvalScopeWithCondition, {
          base,
          names: joinOr(descriptor.accommodation!),
        }),
    },
    {
      present: Boolean(descriptor.condition?.length),
      base: () =>
        intl.formatMessage(msgs.approvalScopeCondition, { names: joinOr(descriptor.condition!) }),
      append: base =>
        intl.formatMessage(msgs.approvalScopeWithCondition, {
          base,
          names: joinOr(descriptor.condition!),
        }),
    },
    {
      present: Boolean(descriptor.configuration?.length),
      base: () =>
        intl.formatMessage(msgs.approvalScopeConfiguration, {
          names: joinOr(descriptor.configuration!),
        }),
      // No dedicated "with configuration" suffix (configuration rarely co-occurs with another
      // name-based attribute — it is itself an occupancy descriptor by NAME rather than by count):
      // fall back to the same condition-style suffix template, which is generic enough ("{base}
      // {names}").
      append: base =>
        intl.formatMessage(msgs.approvalScopeWithCondition, {
          base,
          names: joinOr(descriptor.configuration!),
        }),
    },
    {
      present: Boolean(descriptor.mealPlan?.length),
      base: () =>
        intl.formatMessage(msgs.approvalScopeMealPlan, {
          names: joinMealPlanOr(descriptor.mealPlan!),
        }),
      append: base =>
        intl.formatMessage(msgs.approvalScopeWithMealPlan, {
          base,
          names: joinMealPlanOr(descriptor.mealPlan!),
        }),
    },
    {
      present: Boolean(occupancyPhrase),
      base: () => intl.formatMessage(msgs.approvalScopeOccupancy, { occupancy: occupancyPhrase }),
      append: base =>
        intl.formatMessage(msgs.approvalScopeWithOccupancy, { base, occupancy: occupancyPhrase }),
    },
  ];

  let phrase: string | null = null;
  for (const attr of anchors) {
    if (!attr.present) continue;
    phrase = phrase == null ? attr.base() : attr.append(phrase);
  }
  return phrase;
}

export function formatRate(input: CreatePromotionInput, intl: IntlShape): string {
  if (input.rateType === "PERCENTAGE") {
    return input.percentageValue != null ? `${input.percentageValue}%` : "—";
  }
  if (input.rateType === "CONSTANT") {
    if (input.amount != null) {
      // "día" is its own translatable unit (see approvalRatePerNightUnit) — amount/currency are
      // never translated, only the trailing unit word is.
      const unit = intl.formatMessage(msgs.approvalRatePerNightUnit);
      const amountAndCurrency = [String(input.amount), input.currency].filter(Boolean).join(" ");
      return `${amountAndCurrency}/${unit}`;
    }
  }
  return "—";
}

/**
 * True when the input carries a complete GIFT/GIVE-NIGHT (3x2-style) config — the all-or-nothing
 * giveNight* fields from createPromotionObjectSchema/updateFieldsObject. GIVE_NIGHT promotions
 * have no user-chosen rate/percentage (see rateType's description on the backend), so the create
 * card switches its rate/badge rendering entirely based on this instead of `rateType`.
 */
export function isGiveNightInput(input: {
  giveNightDiscountedUnits?: number;
  giveNightEveryUnits?: number;
  giveNightCriteria?: string;
}): boolean {
  return (
    input.giveNightDiscountedUnits != null &&
    input.giveNightEveryUnits != null &&
    input.giveNightCriteria != null
  );
}

const GIVE_NIGHT_MESSAGES = {
  CHEAPEST: msgs.approvalGiveNightCheapest,
  MOST_EXPENSIVE: msgs.approvalGiveNightMostExpensive,
  LAST: msgs.approvalGiveNightLast,
} as const;

/**
 * Resolves the give-night sentence for a `giveNightCriteria` value. Callers pass their own `b`
 * rich-text renderer to `intl.formatMessage` — a bold JSX one for the "Descuento y alcance"
 * section, a plain-text one (chunks => chunks.join("")) for the badge — exactly like every other
 * `<b>`-carrying message in this file is already consumed at the call site. Falls back to the
 * MOST_EXPENSIVE wording for a defensive, never-should-happen unrecognized/missing criteria
 * (isGiveNightInput being true guarantees a real value in practice) rather than throwing.
 */
export function giveNightMessage(criteria: string | undefined) {
  return (
    GIVE_NIGHT_MESSAGES[criteria as keyof typeof GIVE_NIGHT_MESSAGES] ??
    GIVE_NIGHT_MESSAGES.MOST_EXPENSIVE
  );
}

/**
 * `b` rich-text renderer that collapses back to plain text instead of bold JSX — for contexts
 * that can only render a string, like `CardHeader`'s `badge` prop (see PromotionApprovalSummary's
 * give-night badge, which reuses the SAME `<b>`-carrying messages as the bold "Descuento y
 * alcance" section, just with this renderer instead of a `<strong>`-wrapping one).
 */
export function plainTextTag(chunks: ReactNode): string {
  return Array.isArray(chunks) ? chunks.join("") : String(chunks ?? "");
}

// Maps each window kind to its flat "resulting value" field on the tool input AND its
// section label — the same label used by the create card, so an update's dates render
// identically to a creation's regardless of which of the two carries the data.
export const WINDOW_FIELDS = {
  travelWindows: { flatField: "openToTravelWindows", label: msgs.approvalStayDates },
  bookingWindows: { flatField: "openToBookingWindows", label: msgs.approvalBookingDates },
  closeTravelWindows: { flatField: "closeToTravelWindows", label: msgs.approvalCloseTravelWindow },
  closeBookingWindows: {
    flatField: "closeToBookingWindows",
    label: msgs.approvalCloseBookingWindow,
  },
} as const;

// Shape shared by a multi-ticker entry (MultiPromotionChangeEntry) and a chain multi-entry row
// (ChainUpdateEntry) — both carry the SAME resulting-window flat fields plus `changes`, so a
// single helper can resolve "this entry's windows for this WINDOW_FIELDS key" for either card,
// the same fallback logic UpdateApprovalSummary's own `windowsByKey` uses for a single promotion.
export type WithWindowFields = {
  changes?: PromotionChanges;
  openToBookingWindows?: WindowRange[];
  openToTravelWindows?: WindowRange[];
  closeToBookingWindows?: WindowRange[];
  closeToTravelWindows?: WindowRange[];
  /**
   * Chain-update-only (see ChainUpdateEntry): the per-child inventory breakdown for this entry,
   * present only when the entry's change actually touched inventory. Absent for the multi-ticker
   * flow's MultiPromotionChangeEntry, which never carries it.
   */
  inventoryByChild?: ChainUpdateEntry["inventoryByChild"];
  /**
   * DISPLAY-ONLY, server-computed descriptor of which real attributes (accommodation/condition/
   * mealPlan/occupancy) this entry's inventory-scope change actually matched — see
   * `InventoryScopeDescriptor`'s own doc comment. Lets `describeInventoryScope` build a specific
   * legend ("Habitaciones Dobles") in place of the generic `approvalSelectedRates` fallback, same
   * as the create card. Currently only ever populated for the single-ticker update flow; absent
   * (and safely ignored) for bulk/chain entries until they're wired the same way.
   */
  inventoryScopeDescriptor?: InventoryScopeDescriptor;
};

export function windowsForEntry(
  entry: WithWindowFields,
  key: keyof typeof WINDOW_FIELDS
): WindowRange[] {
  const flat = entry[WINDOW_FIELDS[key].flatField];
  if (Array.isArray(flat) && flat.length > 0) return flat;
  return entry.changes?.[key]?.after ?? [];
}

// Label for every other (non-window) key the backend's `changes` object can carry, EXCEPT
// `translations` — that one is an array of per-locale entries (see `TranslationChangeEntry`),
// not a scalar before/after pair, so it gets its own dedicated rendering below instead of
// going through this generic ChangeRow path. Must stay in lockstep with `PromotionChanges`
// in types.ts.
// `geolocation` is deliberately NOT listed here: it carries a structured {mode, countries}
// descriptor (see GeolocationDescriptor), not a scalar/array/nullable value like every other key
// below, so it can't go through the generic formatChangeValue path. Neither are `rate`,
// `inventoryScope`, `deviceTypes`, `minStay`, `maxStay`, `minNotice`, `maxNotice` — each of these
// now has its OWN dedicated section (see the "Descuento y alcance" discount block, DeviceTypesValue,
// and the unified "Restricciones" block below) instead of a generic before→after text row, so only
// the truly scalar/simple fields remain here.
export const OTHER_CHANGE_LABELS = {
  codes: msgs.approvalPromoCode,
  group: msgs.approvalGroup,
  isVisible: msgs.approvalVisibility,
} as const;

// Shallow, order-sensitive array equality for primitive arrays (string[]/number[] diffs like
// codes/deviceTypes). Replaces an earlier `JSON.stringify(a) === JSON.stringify(b)` comparison —
// serializing both sides on every field of every changed entry was needless allocation/parsing
// work for what these arrays actually are (short lists of primitives); a direct element-by-element
// comparison does the same job in one linear pass with no intermediate strings.
function arraysEqual(a: unknown[], b: unknown[]): boolean {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    if (a[i] !== b[i]) return false;
  }
  return true;
}

// True when `before` and `after` are the same value once "empty" representations are
// normalized (null, undefined, "" and [] are all treated as the same "nothing"). Guards
// against noise like deviceTypes/codes arriving as {before: null, after: []} — semantically
// unchanged, but not caught by a plain `!= null` presence check.
export function isNoopChange(before: unknown, after: unknown): boolean {
  const normalize = (v: unknown) => {
    if (v == null) return null;
    if (Array.isArray(v)) return v.length === 0 ? null : v;
    if (typeof v === "string" && v === "") return null;
    return v;
  };
  const b = normalize(before);
  const a = normalize(after);
  if (b === null || a === null) return b === a;
  if (Array.isArray(b) && Array.isArray(a)) return arraysEqual(b, a);
  return b === a;
}

// Renders a `changes.<key>.before/after` value as plain text for the generic diff row.
// Only non-window fields reach this — window ranges are always shown via WindowRangesView
// (see WINDOW_FIELDS) so an update's dates look exactly like a creation's, never as a
// plain-text line. `group` is shown without its "GROUP_" prefix, matching the rest of the app.
export function formatChangeValue(
  key: string,
  value: unknown,
  dash: (v: string) => string
): string {
  if (value == null) return dash("");
  if (Array.isArray(value)) {
    if (value.length === 0) return dash("");
    return (value as string[]).join(", ");
  }
  if (key === "group") return dash(String(value).replace(/^GROUP_/, ""));
  return dash(String(value));
}

// The backend's `changes.inventoryScope.before/after` (updateChangesSchema) is free text:
// either the "all inventory" phrase (e.g. "todas las tarifas") or a comma-separated list of
// individual line names. We never want to show the raw list — same reasoning as the create
// card (PromotionApprovalSummary), which shows a line COUNT instead of enumerating lines — so
// this detects the "all" phrase and otherwise counts the comma-separated entries.
export function isAllInventoryPhrase(value: string, allLabel: string): boolean {
  const v = value.trim().toLowerCase();
  return v === allLabel.trim().toLowerCase() || v === "todas las tarifas" || v === "all rates";
}

/**
 * `realCount`, when given, is ALWAYS preferred over counting commas in `value` — the backend now
 * CAPS `before`/`after` at a small fixed placeholder count regardless of the real scope size (see
 * `describeScope`'s doc comment in the backend), so counting commas silently undercounts any
 * scope bigger than that cap. Pass the matching `beforeCount`/`afterCount` whenever it's
 * available; the comma-count is only a fallback for entries that predate those fields entirely.
 */
export function inventoryScopeCountLabel(
  value: string,
  allLabel: string,
  intl: IntlShape,
  realCount?: number
): string {
  if (!value) return "—";
  if (isAllInventoryPhrase(value, allLabel)) return allLabel;
  const count =
    typeof realCount === "number"
      ? realCount
      : value
          .split(",")
          .map(s => s.trim())
          .filter(Boolean).length;
  return intl.formatMessage(msgs.approvalInventoryLines, { count });
}

/**
 * Mode-aware counterpart of `inventoryScopeCountLabel` — takes the backend's `affectedCount`
 * (see `InventoryScopeChangeEntry`'s doc comment) directly, rather than deriving a delta from
 * `beforeCount`/`afterCount` here: `affectedCount` is independently recomputed SERVER-SIDE at
 * write time (see `PromotionTools.ts`'s `withInventoryAffectedCount` in the backend), so it stays
 * correct even if the model's copy of `beforeCount`/`afterCount` in `changes` drifted or was
 * dropped — reproduced live, an "add 1 line" call whose write-time arguments lost track of the
 * true before/after scope rendered as "Se aplica a 451 líneas de inventario" instead of "Se
 * agrega 1 línea de inventario". Names the ACTION for "add"/"remove" ("Removes 12 inventory
 * lines" / "Adds 3 inventory lines") and falls back to the plain count for "replace" (including
 * "apply to ALL inventory", where the resulting total genuinely IS what's affected).
 */
export function inventoryChangeLabel(
  params: {
    mode?: "replace" | "add" | "remove";
    affectedCount: number;
  },
  intl: IntlShape
): string {
  const { mode, affectedCount } = params;
  // A "remove"/"add" call is rejected server-side unless it actually targets at least one line
  // (see the backend's refineUpdateFields/refineChildInventoryOverride guards) — but stay
  // defensive here too: an unexpected 0 falls back to the plain count rather than claiming
  // "Removes 0 inventory lines" / "Adds 0 inventory lines".
  if (mode === "remove" && affectedCount > 0) {
    return intl.formatMessage(msgs.approvalInventoryLinesRemoved, { removed: affectedCount });
  }
  if (mode === "add" && affectedCount > 0) {
    return intl.formatMessage(msgs.approvalInventoryLinesAdded, { added: affectedCount });
  }
  return intl.formatMessage(msgs.approvalInventoryLines, { count: affectedCount });
}

// Resolves the "Descuento y alcance"-style scope for an update entry: whether the rate change
// targets all inventory or a specific selection, plus the caption showing the affected LINE
// COUNT — mirrors the create card's rateScope/lineCount (PromotionApprovalSummary) so an update's
// rate change reads identically to a creation's. Prefers the `changes.inventoryScope` diff (it
// carries the real resulting scope); falls back to the flat resulting fields
// (appliesToAllInventory/appliesToBookable) the single-ticker update flow also carries — the
// multi-ticker/chain entry shapes don't have those flat fields, so `entry` types them optional.
export function resolveDiscountScope(
  entry: {
    changes?: PromotionChanges;
    appliesToAllInventory?: boolean;
    appliesToBookable?: string[];
  },
  intl: IntlShape
): { scopeLabel: string | null; lineCountCaption: string | null } {
  const allInventoryLabel = intl.formatMessage(msgs.approvalAllInventory);
  const inventoryScopeDiff = entry.changes?.inventoryScope;
  let scopeValueRaw: string | undefined;
  // Tracks whether `scopeValueRaw` came from the REAL diff (the only source with mode/
  // before/afterCount attached) vs. one of the flat fallback fields below, which never carry
  // those — needed so the mode-aware caption below is only attempted when it can actually be
  // correct.
  let usedDiff = false;
  if (
    inventoryScopeDiff != null &&
    !isNoopChange(inventoryScopeDiff.before, inventoryScopeDiff.after)
  ) {
    scopeValueRaw = String(inventoryScopeDiff.after);
    usedDiff = true;
  } else if (entry.appliesToAllInventory) {
    scopeValueRaw = allInventoryLabel;
  } else if (Array.isArray(entry.appliesToBookable) && entry.appliesToBookable.length > 0) {
    scopeValueRaw = entry.appliesToBookable.join(", ");
  }
  // No inventory-scope data at all (this update didn't touch inventory, so it's left untouched) —
  // still always show a legend next to the rate rather than a bare number, for consistency with
  // the case where scope data IS present. We don't know the resulting scope here (it wasn't part
  // of this change), so default to "selected rates" rather than asserting "all rates" — there is
  // no line count to caption either.
  if (scopeValueRaw == null) {
    return { scopeLabel: intl.formatMessage(msgs.approvalSelectedRates), lineCountCaption: null };
  }
  const isAll = isAllInventoryPhrase(scopeValueRaw, allInventoryLabel);
  // The caption ALWAYS shows the REAL number of lines this operation affects, via `affectedCount`
  // — including when the scope is "all inventory": that used to just repeat the "Todas las
  // tarifas" label with no number at all, which is exactly the ambiguity `affectedCount` exists to
  // resolve (see its doc comment) — a "replace with ALL inventory" genuinely affects every one of
  // those lines, so showing the real total ("Aplica a 81 líneas de inventario") is correct here,
  // not redundant. Falls back to the legacy comma-count heuristic only for entries that predate
  // `affectedCount` (older persisted messages) or that came from the flat fallback fields below
  // (no diff at all, so there's no backend-computed count to use).
  const lineCountCaption =
    usedDiff && inventoryScopeDiff && typeof inventoryScopeDiff.affectedCount === "number"
      ? inventoryChangeLabel(
          {
            mode: inventoryScopeDiff.operationMode,
            affectedCount: inventoryScopeDiff.affectedCount,
          },
          intl
        )
      : inventoryScopeCountLabel(
          scopeValueRaw,
          allInventoryLabel,
          intl,
          inventoryScopeDiff?.afterCount
        );
  return {
    scopeLabel: isAll ? allInventoryLabel : intl.formatMessage(msgs.approvalSelectedRates),
    lineCountCaption,
  };
}

// A promotion's name/description can arrive as rich-text HTML from the Backoffice's WYSIWYG
// editor (e.g. "<p>Promoci&oacute;n Oto&ntilde;o</p>") rather than plain text — every card here
// only ever shows a short one-line preview, never rich formatting, so tags and entities must be
// stripped/decoded instead of shown verbatim. Parsing through a detached <div> and reading
// `textContent` uses the browser's own HTML parser for both jobs at once (tag stripping AND
// entity decoding) instead of hand-rolling an entity table; it's safe for display purposes even
// though the input is untrusted — a <script> tag created via `innerHTML` is marked "already
// started" per the HTML spec and never executes, detached or not, and we never re-insert the
// parsed nodes into the visible document, only read back their decoded text.
export function htmlToPlainText(html: string | null | undefined): string {
  if (!html) return "";
  if (typeof document === "undefined") return html;
  const container = document.createElement("div");
  container.innerHTML = html;
  return (container.textContent || "").replace(/\s+/g, " ").trim();
}

// Renders one translation's name/description as a single line for the before/after diff.
// `content` is null for the "before" side of a newly-added locale.
export function formatTranslationContent(
  content: { name: string; description: string } | null
): string {
  if (!content) return "—";
  const name = htmlToPlainText(content.name);
  const description = htmlToPlainText(content.description);
  if (name && description) return `${name}: ${description}`;
  return name || description || "—";
}
